import { EntityIdAliasPrefixes } from '@abb-procure/constants';
import {
  InvitedUser,
  InvitedUserModel,
  ShareApiService,
} from '@abb-procure/data';
import { SnackbarService } from '@abb-procure/snackbar';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { DialogService } from '../../services/dialog.service';
import { escapeDialog } from '../../utilities/dialog.util';
import { Button } from '../button/button.component';

export interface InvitedUsersDialogData {
  users: InvitedUserModel[];
  entityType: EntityIdAliasPrefixes;
  id: string;
  canRemove: boolean;
  onRemove: () => void;
}

@Component({
  templateUrl: './invited-users-dialog.component.html',
  styleUrl: './invited-users-dialog.component.scss',
  imports: [MatDialogContent, Button, MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvitedUsersDialog implements OnInit {
  invitedUsers: InvitedUserModel[] = [];

  get users(): InvitedUserModel[] {
    return this.data.users;
  }

  get entityType(): EntityIdAliasPrefixes {
    return this.data.entityType;
  }

  get id(): string {
    return this.data.id;
  }

  get canRemove(): boolean {
    return this.data.canRemove;
  }

  get onRemove(): () => void {
    return this.data.onRemove;
  }

  private readonly dialogRef = inject(
    MatDialogRef<InvitedUsersDialog, InvitedUserModel[]>,
  );

  private readonly data: InvitedUsersDialogData = inject(MAT_DIALOG_DATA);
  private readonly shareApiService = inject(ShareApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialogService = inject(DialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.invitedUsers = this.data.users;
  }

  ngOnInit(): void {
    escapeDialog(this.dialogRef, this.destroyRef, () => this.onClose());
  }

  onClose(): void {
    this.dialogRef.close(this.invitedUsers);
  }

  removeUser(user: InvitedUser): void {
    this.dialogService
      .showConfirmDialog({
        text: `Are you sure you want to remove ${user.userModel.name} from the list of invited users?`,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response) {
          this.handleUserRemoval(user);
        }
      });
  }

  private async handleUserRemoval(user: InvitedUser): Promise<void> {
    if (!user.userModel.id) {
      return;
    }

    const response = await this.shareApiService.removeInvitedUser(
      this.entityType,
      this.id,
      user.userModel.id,
    );

    if (!response) {
      return;
    }

    this.invitedUsers = this.invitedUsers.filter(
      (item) => item.userModel.id !== user.userModel.id,
    );

    this.onRemove();
    this.cdr.markForCheck();

    this.snackbarService.showSuccessSnackbar({
      text: `Removed ${user.userModel.name} from the list of invited users.`,
    });
  }
}
